import { useEffect } from 'react'
import { useLocation } from 'react-router'

const APP_NAME = 'CISO Adapt'

/**
 * Route titles configuration
 * Maps route paths to their display titles
 */
const routeTitles: Record<string, string> = {
  // Common routes
  '/': 'Dashboard',
  '/ai-chat': 'AI Chat',
  '/login-logs': 'Login Logs',
  '/activity-logs': 'Activity Logs',
  '/activity-logs/all-users': 'All Users Activity Logs',
  '/activity-analytics': 'Activity Analytics',
  '/notifications': 'Notifications',
  '/profile': 'Profile',
  '/update-password': 'Update Password',

  // Policy routes
  '/policy': 'Policy',
  '/policy-dashboard': 'Policy Dashboard',

  // User management
  '/manage-users': 'Manage Users',

  // Document repository
  '/document-repo': 'Document Repository',

  '/organisation/ai-model': 'AI Models',

  // Project management
  '/manage-projects': 'Projects',
  '/manage-projects/tasks': 'Tasks',
  '/tasks-dashboard': 'Tasks Dashboard',

  // Meeting minutes
  '/meeting-minutes': 'Meeting Minutes',
  '/meeting-minutes-type': 'Meeting Minutes Type',

  // Staff management
  '/manage-staff': 'Staff List',
  '/manage-department': 'Department',

  // Information asset
  '/information-asset': 'Information Asset',
  '/information-asset-report': 'Information Asset Report',

  // Risk management
  '/risk-list': 'Risk Register',
  '/add-risk': 'Add Risk',
  '/plan-of-action': 'Plan of Action',
  '/risk-dashboard': 'Risk Dashboard',
  '/risk-tracking': 'Risk Tracking',

  // Incident management
  '/incident': 'Incident Register',
  '/add-incident': 'Add Incident',
  '/incident-dashboard': 'Incident Dashboard',

  // Compliance & Assessment
  '/assessments': 'Standards & Assessments',
  '/compliance-report': 'Compliance Report',
  '/report-library': 'Report Library',
  '/archive-assessment': 'Archive Assessment',

  // Supply Chain Management
  '/service-provider-dashboard': 'Supply Chain Management Dashboard',
  '/service-provider-list': 'Suppliers',
  '/add-service-provider': 'Add Supplier',
  '/assessment-library': 'Questionnaire Library',
  '/add-assessment-library': 'Add Questionnaire Library',
  '/service-provider-assessment': 'Supply Chain Assessment',
  '/add-service-provider-assessment': 'Add Supply Chain Assessment',

  // Country regulatory register
  '/country-regulatory-register-oos': 'Country Regulatory Register',
  '/country-regulatory-register-oos/jurisdiction': 'Jurisdiction',
  '/country-regulatory-register-oos/operation-type': 'Operation Type',
  '/country-regulatory-register-oos/regulatory-category': 'Regulatory Category',
  '/country-regulatory-register-oos/region': 'Region',
  '/country-regulatory-register-oos/risk-type': 'Risk Type',

  // Other organization routes
  '/exemptions-register': 'Exemptions Register',
  '/change-management-process': 'Change Management Process',
  '/release-notes': 'Release Notes',
  '/bug-report': 'Bug Report',
  '/organisation/resource': 'Resource Help Desk',
  '/organisation/ai-token-usage': 'AI Token Usages',
  '/organisation/ai-changes': 'AI Changes',
  '/isms-assurance-calendar': 'ISMS Assurance Calendar',

  '/ai-register': 'AI Register',
  '/security-awareness/phished': 'Phished',
  '/security-awareness/mimecast': 'Mimecast',

  // Settings
  '/settings': 'Settings',
  '/settings/security/password-policy': 'Password Policy',
  '/settings/security/signature-settings': 'Signature Settings',
  '/settings/policy/policy-category': 'Policy Category',
  '/settings/policy/policy-frequencies': 'Policy Frequencies',
  '/settings/policy/dashboard-benchmarks': 'Policy Dashboard Benchmarks',
  '/settings/task/task-status': 'Task Status',
  '/settings/task/task-severity': 'Task Severity',
  '/settings/service-provider/service-provider-contract-status': 'Supply Chain Contract Status',
  '/settings/service-provider/service-type': 'Service Type',
  '/settings/service-provider/assessment-tier': 'Assessment Tier',
  '/settings/service-provider/assessment-tier-criticality': 'Supply Chain Criticality',
  '/settings/service-provider/assessment-tier-sensitivity': 'Supply Chain Sensitivity',
  '/settings/risk/risk-likelihood': 'Risk Likelihood',
  '/settings/risk/risk-impact': 'Risk Impact',
  '/settings/risk/risk-rating': 'Risk Rating',
  '/settings/risk/risk-matrix': 'Risk Matrix',
  '/settings/risk/risk-categories': 'Risk Categories',
  '/settings/risk/incident-types': 'Incident Types',
  '/settings/risk/vulnerability': 'Vulnerability',
  '/settings/risk/threat-category': 'Threat Category',
  '/settings/risk/threat-source': 'Threat Source',
  '/settings/risk/threat-actor': 'Threat Actor',
  '/settings/risk/threat': 'Threat',
  '/settings/risk/risk-owners': 'Risk Owners',
  '/settings/risk/risk-identification-source': 'Risk Identification Source',
  '/settings/non-conformity/root-identification': 'Root Identification',
  '/settings/non-conformity/affected-area': 'Affected Area',
  '/settings/non-conformity/importance': 'Importance',
  '/settings/non-conformity/action-status': 'Action Status',
  '/settings/information-asset/asset-type': 'Asset Type',
  '/settings/roles-and-permissions': 'Roles & Permissions',
  '/settings/utility': 'Utility Settings',
  '/settings/external-integration': 'External Integration',
  '/settings/api-tokens': 'API Tokens',
  '/settings/jira-integration': 'Jira Integration',
  '/settings/change-management-process-category': 'Change Management Process Category',
  '/settings/ai-register/type': 'AI Register Type',
  '/settings/ai-register/permitted-data': 'AI Register Permitted Data',
  '/settings/ai-register/screening-questionnaire': 'AI Screening Questionnaire',
  '/settings/azure-integration': 'Azure Integration',
  '/settings/palo-alto-networks-integration': 'Palo Alto Networks Integration',
  '/settings/ms-defender-integration': 'MS Defender Integration',
  '/settings/mimecast-integration': 'Mimecast Integration',
  '/settings/security/sso': 'SSO Setup',
  '/settings/mfa': 'MFA Setup',
  '/settings/profile': 'Profile',

  // CTRA (Cyber Threat Risk Assessment)
  '/ctra/programs': 'Programs',
  '/ctra/zones': 'Zones',
  '/ctra/engagements': 'Engagements',
  '/ctra/engagements/add': 'Engagement',

  // Super admin routes
  '/admin/signup-request': 'Signup Requests',
  '/admin/signup-request/report': 'Signup Request Report',
  '/admin/organisations': 'Organisations',
  '/admin/dashboard-defaults': 'Dashboard Chart Types',
  '/admin/organisation-users': 'Organisation Users',
  '/admin/clerk-users': 'Clerk Users',
  '/admin/assessment': 'Assessments',
  '/admin/admins': 'Admins',
  '/admin/resources': 'Resources',
  '/admin/helpful-tips': 'Helpful Tips',
  '/admin/it-privileged-operations': 'IT Privileged Operations',
  '/admin/release-notes': 'Release Notes',
  '/admin/bug-report': 'Bug Reports',
  '/admin/standards': 'Standards',
  '/admin/saq-types': 'SAQ Types',
  '/admin/fiscal-years': 'Fiscal Years',
  '/admin/standard-levels': 'Standard Levels',
  '/admin/control-groups': 'Control Groups',
  '/admin/controls': 'Controls',
  '/admin/control-mapping': 'Control Mapping',
  '/admin/control-questionnaires': 'Control Questionnaires',
  '/admin/maturity-models': 'Maturity Models',
  '/admin/maturity-levels': 'Maturity Levels',
  '/admin/module': 'Modules',
  '/admin/subscriptions': 'Subscriptions',
  '/admin/subscription-type': 'Subscription Type',
  '/admin/fcm-tokens': 'FCM Tokens',
  '/admin/ai-token-usage': 'AI Token Usage',
  '/admin/api-docs-tokens': 'API Docs Tokens',
  '/admin/user-guide-api-keys': 'User Guide API Keys',
  '/admin/ai-prompt-catalog': 'AI Prompt Catalog',

  // Auth routes (public)
  '/sign-in': 'Sign In',
  '/sign-up': 'Sign Up',
  '/verify-otp': 'Verify OTP',
  '/forget-password': 'Forgot Password',
  '/404': 'Page Not Found',
  '/terms-of-service': 'Terms of Service',
  '/privacy-policy': 'Privacy Policy',

  // Vendor routes
  '/vendor/assessments': 'Vendor Assessments',
}

/**
 * Gets the title for a given path, handling dynamic routes
 * @param pathname - The current pathname from useLocation
 * @returns The title for the page
 */
const getTitleFromPath = (pathname: string): string => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'

  // Direct match
  if (routeTitles[normalizedPath]) {
    return routeTitles[normalizedPath]
  }

  // Handle dynamic routes with parameters
  const dynamicRoutePatterns: { pattern: RegExp; title: string }[] = [
    { pattern: /^\/on-boarding\/[^/]+$/, title: 'Onboarding' },
    { pattern: /^\/document-repo\/[^/]+\/folder$/, title: 'Folder' },
    { pattern: /^\/document-repo\/[^/]+\/folder\/[^/]+\/sub-folder$/, title: 'Sub Folder' },
    { pattern: /^\/add-risk\/[^/]+$/, title: 'Register New Risk' },
    { pattern: /^\/add-incident\/[^/]+$/, title: 'Register New Incident Incident' },
    { pattern: /^\/add-service-provider\/[^/]+$/, title: 'Supply Chain Management' },
    { pattern: /^\/add-assessment-library\/[^/]+$/, title: 'Assessment Library' },
    { pattern: /^\/add-service-provider-assessment\/[^/]+$/, title: 'Supply Chain Assessment' },
    { pattern: /^\/assessment-library\/questionnaire-builder\/[^/]+$/, title: 'Questionnaire Builder' },
    { pattern: /^\/assessments\/start-assessments\/[^/]+$/, title: 'Start Assessment' },
    { pattern: /^\/assessments\/start-pci-assessment\/[^/]+$/, title: 'Start PCI Assessment' },
    { pattern: /^\/assessments\/start-pci-roc-assessment\/[^/]+$/, title: 'PCI ROC Assessment' },
    { pattern: /^\/assessments\/start-ism-irap-assessment\/[^/]+$/, title: 'ISM IRAP Assessment' },
    { pattern: /^\/assessments\/start-ism-gap-assessment\/[^/]+$/, title: 'ISM GAP Assessment' },
    { pattern: /^\/assessments\/start-cis-assessment\/[^/]+$/, title: 'CIS GAP Assessment' },
    { pattern: /^\/assessments\/start-soc2-assessment\/[^/]+$/, title: 'SOC 2 GAP Assessment' },
    { pattern: /^\/assessments\/start-csp-assessment\/[^/]+$/, title: 'CSP Assessment' },
    { pattern: /^\/assessments\/csp-assessment\/review\/[^/]+\/controls$/, title: 'CSP Control Review' },
    { pattern: /^\/assessments\/csp-assessment\/review\/[^/]+$/, title: 'CSP Assessment Review' },
    { pattern: /^\/assessments\/csp-assessment\/attestation\/[^/]+$/, title: 'CSP Assessment Attestation' },
    { pattern: /^\/assessments\/csp-assessment\/exception-request\/[^/]+$/, title: 'CSP Exception Request' },
    { pattern: /^\/assessments\/csp-assessment\/[^/]+$/, title: 'CSP Assessment' },
    { pattern: /^\/assessments\/[^/]+$/, title: 'Assessment Detail' },
    { pattern: /^\/assessments\/pci-assessment\/[^/]+$/, title: 'PCI ROC Assessment Detail' },
    { pattern: /^\/assessments\/ism-irap-assessment\/[^/]+$/, title: 'ISM IRAP Assessment Detail' },
    { pattern: /^\/assessments\/ism-principle-assessment\/[^/]+$/, title: 'ISM Principles Assessment Detail' },
    { pattern: /^\/ai-assessment\/[^/]+$/, title: 'AI Assessment' },
    { pattern: /^\/reporting\/[^/]+$/, title: 'Reporting' },
    { pattern: /^\/pci-roc-report\/[^/]+$/, title: 'PCI ROC Report' },
    { pattern: /^\/assessments\/logs\/[^/]+$/, title: 'Assessment Logs' },
    { pattern: /^\/non-conformity\/[^/]+$/, title: 'Non-Conformity' },
    { pattern: /^\/audit-list\/[^/]+$/, title: 'Audit List' },
    { pattern: /^\/audit\/[^/]+$/, title: 'Audit' },
    { pattern: /^\/policy\/secure\/[^/]+$/, title: 'Policy Review' },
    { pattern: /^\/policy\/review-request\/[^/]+$/, title: 'Policy Review Request' },
    { pattern: /^\/policy\/approval-request\/[^/]+$/, title: 'Policy Approval Request' },
    { pattern: /^\/service-provider-assessment\/secure\/[^/]+$/, title: 'Supply Chain Assessment Review' },
    { pattern: /^\/admin\/organisations\/child-organisations\/[^/]+$/, title: 'Child Organisation' },
    { pattern: /^\/admin\/risk-assessment\/questionnaire-library\/[^/]+$/, title: 'Questionnaire Library' },
    { pattern: /^\/admin\/people-risk-assessment\/questionnaire-library\/[^/]+$/, title: 'Questionnaire Library' },
    { pattern: /^\/assessments\/pci-scoping-configuration\/[^/]+$/, title: 'PCI Scoping Configuration' },
    { pattern: /^\/assessments\/pci-scoping-configuration\/[^/]+\/[^/]+$/, title: 'PCI Scoping Configuration' },
    { pattern: /^\/assessments\/ism-scoping-configuration\/[^/]+$/, title: 'ISM Scoping Configuration' },
    { pattern: /^\/assessments\/ism-scoping-configuration\/[^/]+\/[^/]+$/, title: 'ISM Scoping Configuration' },
    { pattern: /^\/assessments\/start-pci-assessment\/[^/]+$/, title: 'Start PCI Assessment' },
    { pattern: /^\/assessments\/start-pci-roc-assessment\/[^/]+$/, title: 'PCI ROC Assessment' },
    { pattern: /^\/assessments\/start-pci-saq-assessment\/[^/]+$/, title: 'PCI SAQ Assessment' },
    { pattern: /^\/assessments\/pci-saq-assessment\/[^/]+$/, title: 'PCI SAQ Assessment' },
    { pattern: /^\/assessments\/saq-report\/[^/]+$/, title: 'PCI SAQ Report' },
    { pattern: /^\/meeting-minutes\/review-request\/[^/]+$/, title: 'Meeting Minutes Review Request' },
    { pattern: /^\/vendor\/assessments\/[^/]+$/, title: 'Assessment Detail' },
    { pattern: /^\/ctra\/engagements\/add\/[^/]+$/, title: 'Engagement' },
    { pattern: /^\/service-provider-incoming-questionnaire\/[^/]+$/, title: 'Incoming Questionnaire' },
    { pattern: /^\/assessments\/pci-saq-assessment\/[^/]+$/, title: 'PCI SAQ Assessment' },
    { pattern: /^\/assessments\/csp-assessment\/dashboard\/[^/]+$/, title: 'CSP Dashboard' },
    { pattern: /^\/ctra\/engagements\/[^/]+$/, title: 'Engagement Overview' },
    { pattern: /^\/admin\/user-guides\/[^/]+$/, title: 'User Guide Detail' },
    { pattern: /^\/policy\/secure-bundle\/[^/]+$/, title: 'Policy Acknowledgement' },
    { pattern: /^\/archive-assessment\/[^/]+$/, title: 'Archive Assessment' },
  ]

  for (const { pattern, title } of dynamicRoutePatterns) {
    if (pattern.test(normalizedPath)) {
      return title
    }
  }

  // Fallback: Generate title from path
  const pathSegments = normalizedPath.split('/').filter(Boolean)
  if (pathSegments.length > 0) {
    const lastSegment = pathSegments[pathSegments.length - 1]
    // Convert kebab-case to Title Case
    return lastSegment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return 'Dashboard'
}

export { getTitleFromPath }

/**
 * @param customTitle - Optional custom title to override the route-based title
 */
export const usePageTitle = (customTitle?: string) => {
  const location = useLocation()

  useEffect(() => {
    const title = customTitle || getTitleFromPath(location.pathname)
    document.title = `${title} | ${APP_NAME}`

    // Cleanup - reset to app name when component unmounts
    return () => {
      document.title = APP_NAME
    }
  }, [location.pathname, customTitle])
}

/**
 * Sets the page title manually (for use outside of React components)
 * @param title - The title to set
 */
export const setPageTitle = (title: string) => {
  document.title = `${title} | ${APP_NAME}`
}

export default usePageTitle
